import React, { useState, useCallback, memo, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

import p1 from '../assets/illustration-webp/ufa-dump.webp';
import p2 from '../assets/illustration-webp/vic-mils.webp';
import p3 from '../assets/illustration-webp/celsius.webp';
import p4 from '../assets/illustration-webp/bms-miasin.webp';
import p5 from '../assets/illustration-webp/backgound-pop-bms.webp';
import p6 from '../assets/illustration-webp/cks-mils.webp';
import p7 from '../assets/illustration-webp/bms-miasin-blanc.webp';
import p8 from '../assets/illustration-webp/bissap-miasin.webp';
import p9 from '../assets/illustration-webp/bms-miasin-group.webp';
import p10 from '../assets/illustration-webp/bms-miasin-large.webp';
import p11 from '../assets/illustration-webp/bms-miasin-noir.webp';
import p13 from '../assets/illustration-webp/cks-show-2.webp';
import p14 from '../assets/illustration-webp/mils-bissap.webp';
import p15 from '../assets/illustration-webp/mils-elo.webp';
import p16 from '../assets/illustration-webp/shadow-bissap.webp';
import p17 from '../assets/illustration-webp/show-miasin.webp';
import p18 from '../assets/illustration-webp/snake-lovee.webp';

const imageData = [
  { src: p1, credit: '© @idamah_' },
  { src: p2, credit: '© @idamah_' },
  { src: p3, credit: '© @idamah_' },
  { src: p4, credit: '© @chersmokes' },
  { src: p5, credit: '© @idamah_' },
  { src: p6, credit: '© @chersmokes' },
  { src: p7, credit: '© @chersmokes' },
  { src: p8, credit: '© @chersmokes' },
  { src: p9, credit: '© @chersmokes' },
  { src: p10, credit: '© @chersmokes' },
  { src: p11, credit: '© @chersmokes' },
  { src: p13, credit: '© @chersmokes' },
  { src: p14, credit: '© @chersmokes' },
  { src: p15, credit: '© @idamah_' },
  { src: p16, credit: '© @chersmokes' },
  { src: p17, credit: '© @chersmokes' },
  { src: p18, credit: '© @idamah_' }
];

const GalleryImage = memo(({ image, index, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div 
      className="group relative rounded-lg shadow-lg overflow-hidden bg-gray-800/20 transition-transform duration-300 hover:scale-105 cursor-pointer"
      onClick={() => onClick(image)}
    >
      {/* Placeholder shimmer */}
      <div 
        className={`absolute inset-0 bg-gradient-to-r from-gray-800/20 to-gray-700/20 animate-pulse
          ${isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}
      />
      
      <LazyLoadImage
        src={image.src}
        alt={`Événement ${index + 1}`}
        className={`w-full h-full object-cover transition-all duration-700 ease-out
          ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
        afterLoad={() => setIsLoaded(true)}
        threshold={100}
      />

      {/* Overlay au hover */}
      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
});

const ImageModal = memo(({ image, onClose }) => {
  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
      onClick={onClose}
    >
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div 
          className="relative flex items-center justify-center"
          onClick={e => e.stopPropagation()}
        >
          <LazyLoadImage
            src={image.src}
            alt="Image en grand"
            effect="blur"
            className="max-w-[80vw] max-h-[80vh] w-auto h-auto object-contain shadow-xl rounded"
          />
          <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded text-white text-sm">
            {image.credit}
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full hover:bg-black/80 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>
    </div>
  );
});

const BlackMafia = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [visibleImages, setVisibleImages] = useState(6);

  const openImage = useCallback((image) => {
    document.body.style.overflow = 'hidden';
    setSelectedImage(image);
  }, []);

  const closeImage = useCallback(() => {
    document.body.style.overflow = 'auto';
    setSelectedImage(null);
  }, []);

  const loadMore = useCallback(() => {
    setVisibleImages(prev => Math.min(prev + 6, imageData.length));
  }, []);

  return (
    <section className="events-gallery py-16 px-6 text-gray-100 mt-4">
      <p className="text-xl font-bold text-center text-gray-300 mb-8">
        Pour nous, les supporters font partie intégrante de BMS. Retour en image sur notre communauté !
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 auto-rows-[250px]">
        {imageData.slice(0, visibleImages).map((image, index) => (
          <GalleryImage
            key={index}
            image={image}
            index={index}
            onClick={openImage}
          />
        ))}
      </div>

      {visibleImages < imageData.length && (
        <button 
          onClick={loadMore}
          className="mt-8 mx-auto flex items-center gap-2 px-6 py-3 bg-[#e0e0e0] text-[#090101] text-[16px] font-[600] tracking-[1.5px] rounded-lg hover:bg-[#d32f2f] hover:text-[#e0e0e0] transition-colors group"
        >
          Voir plus d'images
          <ChevronDown 
            size={20} 
            className="transition-transform duration-300 group-hover:translate-y-1"
          />
        </button>
      )}

      {selectedImage && <ImageModal image={selectedImage} onClose={closeImage} />}
    </section>
  );
};

export default BlackMafia;